import React, { useEffect, useState } from 'react';
import { TopicArea } from '../types';
import { getTopicAreas, deleteTopicArea } from '../api/topicAreaApi';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getMyFavorites, removeFavorite, FavoriteTopicArea } from '../api/favoriteApi';
import './TopicAreaListPage.css';

const TopicAreaListPage: React.FC = () => {
  const { user } = useAuth();
  const [topicAreas, setTopicAreas] = useState<TopicArea[]>([]);
  const [favorites, setFavorites] = useState<FavoriteTopicArea[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchTopicAreas = async () => {
    setLoading(true);
    try {
      const data = await getTopicAreas();
      setTopicAreas(data);
      
      if (user) {
        const favs = await getMyFavorites();
        setFavorites(favs);
      }
    } catch (err) {
      console.error('데이터 로딩 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopicAreas();
  }, [user]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await deleteTopicArea(id);
    fetchTopicAreas();
  };

  const handleRemoveFavorite = async (e: React.MouseEvent, topicAreaId: number) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await removeFavorite(topicAreaId);
      if (user) {
        const favs = await getMyFavorites();
        setFavorites(favs);
      }
    } catch (err) {
      console.error('관심 해제 실패:', err);
    }
  };

  const handleCardKeyDown = (e: React.KeyboardEvent, path: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(path);
    }
  };

  return (
    <div className="topic-list-container">
      <div className="topic-list-header">
        <h2>주제영역 아카이브 목록</h2>
        <button 
          onClick={() => navigate('/topic-areas/new')} 
          className="modern-btn modern-btn-primary"
        >
          ✨ + 주제영역 등록
        </button>
      </div>

      {user && favorites.length > 0 && (
        <div className="favorite-section" style={{ marginBottom: 48 }}>
          <h3 className="favorite-section-title">⭐ 관심 주제영역</h3>
          <ul className="topic-card-list">
            {favorites.map(fav => (
              <li
                key={fav.id}
                className="topic-card-item favorite-card"
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/topic-areas/${fav.topic_area_id}`)}
                onKeyDown={(e) => handleCardKeyDown(e, `/topic-areas/${fav.topic_area_id}`)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="topic-card-title">
                    <Link to={`/topic-areas/${fav.topic_area_id}`} onClick={(e) => e.stopPropagation()}>{fav.title}</Link>
                  </div>
                  <button
                    onClick={(e) => handleRemoveFavorite(e, fav.topic_area_id)}
                    className="fav-remove-btn"
                    title="관심 해제"
                  >
                    ★
                  </button>
                </div>
                <div className="topic-card-summary">{fav.summary}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="topic-card-date">
                    등록일: {new Date(fav.created_at).toLocaleDateString('ko-KR')}
                  </div>
                  <Link to={`/timeline/${fav.topic_area_id}`} className="favorite-timeline-link" onClick={(e) => e.stopPropagation()}>
                    타임라인 이동 ➔
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      <h3 className="list-subtitle" style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>📋 전체 주제영역 목록</h3>
      
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', fontWeight: 'bold', color: 'var(--text-secondary)' }}>
          Loading...
        </div>
      ) : topicAreas.length === 0 ? (
        <div className="topic-list-empty">
          등록된 주제영역이 없습니다. 새로운 영역을 먼저 등록해 보세요!
        </div>
      ) : (
        <ul className="topic-card-list">
          {topicAreas.map(area => (
            <li
              key={area.id}
              className="topic-card-item"
              role="link"
              tabIndex={0}
              onClick={() => navigate(`/topic-areas/${area.id}`)}
              onKeyDown={(e) => handleCardKeyDown(e, `/topic-areas/${area.id}`)}
            >
              <div className="topic-card-title">
                <Link to={`/topic-areas/${area.id}`} onClick={(e) => e.stopPropagation()}>{area.title}</Link>
              </div>
              <div className="topic-card-summary">{area.summary}</div>
              <div className="topic-card-date">
                등록일: {new Date(area.created_at).toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TopicAreaListPage;
